// „Къде" и „кой" за периода — двете разбивки под обзора.
//
// Всеки ред е бутон: клик по модул или по човек стеснява списъка до него,
// втори клик го връща. Това е най-краткият път от „нещо е станало снощи" до
// „ето кои двайсет реда са".

import { useTranslation } from "react-i18next";

import type {
  AuditLogModule,
  AuditLogModuleStat,
  AuditLogUserStat,
} from "../../../../api/auditLogs";
import { moduleLabelKey } from "./auditTaxonomy";

const rowClass = (active: boolean) =>
  `flex w-full items-center gap-3 rounded-lg px-2 py-[7px] text-left transition-colors ${
    active ? "bg-brand-tint" : "hover:bg-fill"
  }`;

const Bar = ({ share }: { share: number }) => (
  <span className="h-1.5 w-full overflow-hidden rounded-full bg-fill-2">
    {/* Ширината е дял от най-активния ред — стойност от данните. */}
    <span
      className="block h-full rounded-full bg-brand-tint2"
      style={{ width: `${share}%` }}
    />
  </span>
);

const ProblemBadge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="rounded-md bg-danger-tint px-1.5 py-[2px] font-pos-mono text-[10.5px] font-bold leading-[normal] tabular-nums text-danger-ink">
      {count}
    </span>
  ) : null;

const Panel = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <section className="flex min-w-0 flex-col gap-1.5 rounded-[14px] border border-shade-2 bg-surface p-4">
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {title}
      </h2>
      {hint ? (
        <span className="text-[11px] font-medium text-muted">{hint}</span>
      ) : null}
    </div>
    <div className="flex flex-col">{children}</div>
  </section>
);

type Props = {
  modules: AuditLogModuleStat[];
  users: AuditLogUserStat[];
  selectedModules: AuditLogModule[];
  selectedUserIds: number[];
  onToggleModule: (module: AuditLogModule) => void;
  onToggleUser: (userId: number) => void;
};

export const AuditBreakdown = ({
  modules,
  users,
  selectedModules,
  selectedUserIds,
  onToggleModule,
  onToggleUser,
}: Props) => {
  const { t } = useTranslation();
  const moduleMax = modules.reduce((max, row) => Math.max(max, row.count), 0);
  const userMax = users.reduce((max, row) => Math.max(max, row.count), 0);

  const empty = (
    <p className="px-2 py-3 text-[12px] font-medium text-muted">
      {t("auditLog.noResults")}
    </p>
  );

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Panel
        title={t("auditLog.breakdown.byModule")}
        hint={t("auditLog.breakdown.clickHint")}
      >
        {modules.length === 0 ? empty : null}
        {modules.map((row) => {
          const active = selectedModules.includes(row.module);
          return (
            <button
              key={row.module}
              type="button"
              onClick={() => onToggleModule(row.module)}
              aria-pressed={active}
              className={rowClass(active)}
            >
              <span
                className={`w-[124px] shrink-0 truncate text-[12.5px] leading-[1.2] ${
                  active ? "font-bold text-brand-deep-ink" : "font-semibold text-ink"
                }`}
              >
                {t(moduleLabelKey(row.module))}
              </span>
              <Bar share={moduleMax > 0 ? (row.count / moduleMax) * 100 : 0} />
              <ProblemBadge count={row.problems} />
              <span className="w-[52px] shrink-0 text-right font-pos-mono text-[12px] font-semibold tabular-nums text-soft">
                {row.count}
              </span>
            </button>
          );
        })}
      </Panel>

      <Panel
        title={t("auditLog.breakdown.byUser")}
        hint={t("auditLog.breakdown.clickHint")}
      >
        {users.length === 0 ? empty : null}
        {users.map((row) => {
          const active =
            row.user_id !== null && selectedUserIds.includes(row.user_id);
          return (
            <button
              key={row.user_id ?? "anonymous"}
              type="button"
              disabled={row.user_id === null}
              onClick={() => row.user_id !== null && onToggleUser(row.user_id)}
              aria-pressed={active}
              className={`${rowClass(active)} disabled:cursor-default disabled:hover:bg-transparent`}
            >
              <span
                className={`w-[124px] shrink-0 truncate text-[12.5px] leading-[1.2] ${
                  active ? "font-bold text-brand-deep-ink" : "font-semibold text-ink"
                }`}
              >
                {row.user_name ?? t("auditLog.breakdown.anonymous")}
              </span>
              <Bar share={userMax > 0 ? (row.count / userMax) * 100 : 0} />
              <ProblemBadge count={row.problems} />
              <span className="w-[52px] shrink-0 text-right font-pos-mono text-[12px] font-semibold tabular-nums text-soft">
                {row.count}
              </span>
            </button>
          );
        })}
      </Panel>
    </div>
  );
};
